/**
 * Der Beitritt über den Einladungslink — §43, §44, §67 Schritte 14 bis 17.
 *
 * **Kein Konto, kein Anmelden** (§65): Link öffnen, Namen eingeben, Farbe wählen, drin sein. Der
 * Link **ist** die Berechtigung — eine zusätzliche Hürde davor würde genau den Usecase zerstören,
 * für den es ihn gibt (`CLAUDE.md`, Berechtigungsmodell).
 *
 * **Die Lobby ist schon vor dem Beitritt sichtbar.** Der Einladungs-Token berechtigt dazu
 * (`GET /games/lobby`), und §44 will es so: wer den Link öffnet, soll sehen, wer da ist und
 * welche Farben frei sind — *bevor* er sich entscheidet. Belegte Farben werden deshalb gar nicht
 * erst angeboten; der Server wiese sie ohnehin ab (`SECTOR_TAKEN`).
 *
 * **Die Farbwahl ist optional** (Nr. 29): wer keine wählt, wartet in der Lobby, bis er wählt oder
 * der Host ihm eine zuordnet. „Keine Farbe" heißt dabei **Feld weglassen** und nicht `null` —
 * `null` ist in dieser Schnittstelle dem Host vorbehalten und bedeutet dort „lösen".
 *
 * **Beim Wählen steht das Team dabei** (§8.3, §44): wer eine Farbe nimmt, nimmt damit ein Team.
 *
 * **Eine gespeicherte Sitzung einer anderen Partie wird beiseitegelegt.** Es gibt genau eine
 * Sitzung je Browser (`session.ts`); wer nach Partie eins einen Link zu Partie zwei bekommt,
 * säße sonst vor der Lobby von Partie eins und verstünde nicht, warum. Der gespeicherte Token
 * bleibt, bis der Beitritt ihn ersetzt — bis dahin ist er für die andere Partie noch gültig.
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { LobbyView, SectorIdDto } from '@flaghex/contracts';

import { useRequest } from '../api/context.js';
import { joinGame, lobbyByInvite } from '../api/endpoints.js';
import type { ApiFailure } from '../api/transport.js';
import { GamePanel } from '../game/GamePanel.js';
import { LobbyPanel } from '../lobby/LobbyPanel.js';
import { useConnection } from '../realtime/useConnection.js';
import { readSession, writeSession } from '../session.js';

export interface JoinScreenProps {
  readonly inviteToken: string;
}

export function JoinScreen({ inviteToken }: JoinScreenProps) {
  const { t } = useTranslation(['lobby', 'sectors', 'errors', 'common', 'status']);
  const request = useRequest();

  const [session, setSession] = useState<string | undefined>(() => readSession());
  const [invited, setInvited] = useState<LobbyView | undefined>(undefined);
  const [name, setName] = useState('');
  const [sector, setSector] = useState('');
  const [failure, setFailure] = useState<ApiFailure | undefined>(undefined);
  const [inviteFailure, setInviteFailure] = useState<ApiFailure | undefined>(undefined);

  const { view, status, send } = useConnection(session);

  useEffect(() => {
    let current = true;
    void lobbyByInvite(request, inviteToken).then((result) => {
      if (!current) return;
      if (result.kind === 'ok') {
        setInvited(result.value);
        return;
      }
      setInviteFailure(result);
    });
    return () => {
      current = false;
    };
  }, [request, inviteToken]);

  useEffect(() => {
    if (status === 'unauthenticated') {
      setSession(undefined);
    }
  }, [status]);

  // Siehe Modulkopf: eine Sitzung, die zu einer anderen Partie gehört, wird beiseitegelegt.
  useEffect(() => {
    if (invited !== undefined && view.gameId !== undefined && view.gameId !== invited.gameId) {
      setSession(undefined);
    }
  }, [invited, view.gameId]);

  const join = useCallback((): void => {
    if (name.trim() === '') {
      return;
    }
    setFailure(undefined);
    const body = {
      displayName: name.trim(),
      // Ohne Farbe heißt: Feld weglassen (Nr. 29).
      ...(sector === '' ? {} : { sector: sector as SectorIdDto }),
    };
    void joinGame(request, inviteToken, body).then((result) => {
      if (result.kind !== 'ok') {
        setFailure(result);
        return;
      }
      writeSession(result.value.sessionToken);
      setSession(result.value.sessionToken);
    });
  }, [name, sector, request, inviteToken]);

  const joined = invited !== undefined && view.lobby?.gameId === invited.gameId;

  if (inviteFailure !== undefined) {
    return (
      <main>
        <h1>{t('lobby:playerHeading')}</h1>
        <p role="alert">{t(`errors:${inviteFailure.code}`)}</p>
      </main>
    );
  }

  if (joined && view.lobby !== undefined) {
    // Ab `SETUP` entscheidet der Status (siehe `HostScreen`): die Lobby ist vorbei, das Brett
    // beginnt. Der Stand kommt aus dem **Zustand** — der Übergang `SETUP → PLAYING` schickt
    // keinen neuen Lobby-Stand.
    const phase = view.state?.status ?? view.lobby.status;
    if (phase !== 'CREATED' && phase !== 'LOBBY') {
      /*
        Der Spielbildschirm bringt seit Zyklus D2b seine **eigene** Kopfleiste mit: Phase, Stand und
        wer am Zug ist. Eine Überschrift des Bildschirms darüber wäre eine zweite — genau daran ist
        in Zyklus 10g die Sichtprüfung hängengeblieben („Partie einrichten" über einer laufenden
        Partie).
      
        Der Rückfall bleibt für den Fall **ohne eigenen Zustand**: der Host, der die Partie angelegt
        hat, aber nicht mitspielt. `GamePanel` zeigt dann nur „wird geladen"; die Phase steht
        trotzdem in der Lobby-Ansicht.
      */
      return view.state === undefined ? (
        <main>
          <h1>{t('lobby:playingHeading')}</h1>
          <p role="status">{t('lobby:statusLabel', { status: t(`status:${phase}`) })}</p>
          <GamePanel view={view} send={send} />
        </main>
      ) : (
        <GamePanel view={view} send={send} />
      );
    }
    return (
      <main>
        <h1>{t('lobby:playerHeading')}</h1>
        <LobbyPanel lobby={view.lobby} connected={view.connectedPlayers} />
        <p role="status">{t('lobby:join.waitingForStart')}</p>
      </main>
    );
  }

  if (invited === undefined) {
    return (
      <main>
        <h1>{t('lobby:playerHeading')}</h1>
        <p role="status">{t('common:loading')}</p>
      </main>
    );
  }

  const free = invited.slots.filter((slot) => slot.player === undefined);

  return (
    <main>
      <h1>{t('lobby:playerHeading')}</h1>

      <section>
        <h2>{t('lobby:join.heading')}</h2>
        <p>{t('lobby:join.intro')}</p>
        <p>
          <label>
            {t('lobby:join.name')}{' '}
            <input
              type="text"
              data-field="joinName"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </label>
        </p>
        <p>
          <label>
            {t('lobby:join.sector')}{' '}
            <select
              data-field="joinSector"
              value={sector}
              onChange={(event) => setSector(event.target.value)}
            >
              <option value="">{t('lobby:join.noSector')}</option>
              {free.map((slot) => (
                <option key={slot.sector} value={slot.sector}>
                  {slot.team === undefined
                    ? t(`sectors:${slot.sector}`)
                    : `${t(`sectors:${slot.sector}`)} · ${t('lobby:slots.team', { team: slot.team })}`}
                </option>
              ))}
            </select>
          </label>
        </p>
        <p>{t('lobby:join.noSectorHint')}</p>

        {failure === undefined ? null : (
          <div role="alert">
            <p>{t('lobby:join.failed')}</p>
            <p>{t(`errors:${failure.code}`)}</p>
          </div>
        )}

        <p>
          <button type="button" onClick={join}>
            {t('lobby:join.submit')}
          </button>
        </p>
      </section>

      <LobbyPanel lobby={invited} />
    </main>
  );
}
